import React from 'react';
import { FacilityProtectionResult } from '@types';
import { ICONS } from '../constants';
import UnifiedMap from '../UnifiedMap';
import useGoogleMaps from '../useGoogleMaps';
import { Section, md } from './ReportShared';
import { normalizeISO3 } from '../../utils/geography';
import { COUNTRY_CODES } from '../../common/data';
import { buildFacilityFsatData, buildFacilityThreatAlerts } from '../../utils/facilityProtection';

interface FacilityProtectionDisplayProps {
    result: FacilityProtectionResult;
    onStartFsatAudit?: (planData: any) => void;
    onRefreshConfidence?: () => void;
    refreshingConfidence?: boolean;
    confidenceRefreshError?: string;
}

const FacilityProtectionDisplay: React.FC<FacilityProtectionDisplayProps> = ({ result, onStartFsatAudit,
    onRefreshConfidence, refreshingConfidence, confidenceRefreshError }) => {
    const { isLoaded } = useGoogleMaps();
    const securityControls = result.securityControls || { perimeter: '', accessControl: '', surveillance: '' };
    const keyAssets = Array.isArray(result.keyAssets) ? result.keyAssets : [];
    const threatAssessment = Array.isArray(result.threatAssessment) ? result.threatAssessment : [];
    const assetRegister = Array.isArray(result.assetRegister) ? result.assetRegister : [];
    const threatAssetMatrix = Array.isArray(result.threatAssetMatrix) ? result.threatAssetMatrix : [];
    const countermeasurePlan = Array.isArray(result.countermeasurePlan) ? result.countermeasurePlan : [];
    const complianceGaps = Array.isArray(result.complianceGaps) ? result.complianceGaps : [];
    const roadmap = Array.isArray(result.roadmap) ? result.roadmap : [];
    const enrichmentStatus = Array.isArray(result.enrichmentStatus) ? result.enrichmentStatus : [];
    const countryName = result.location?.address?.split(',').map(part => part.trim()).filter(Boolean).at(-1) || '';
    const facilityIso3 = (COUNTRY_CODES as any)[countryName]?.iso3 || normalizeISO3(countryName) || null;
    const hasValidLocation = Number.isFinite(Number(result.location?.lat))
        && Number.isFinite(Number(result.location?.lng))
        && (Number(result.location?.lat) !== 0 || Number(result.location?.lng) !== 0);
    const validMapMarkers = result.mapMarkers?.filter(marker => Number.isFinite(Number(marker.lat)) && Number.isFinite(Number(marker.lng))) || [];
    const mapMarkers = validMapMarkers.length ? validMapMarkers : (hasValidLocation ? [{
        id: 'facility-loc',
        lat: result.location.lat,
        lng: result.location.lng,
        name: result.facilityName,
        color: '#EF4444',
        type: 'Facility'
    }] : []);
    const localThreatAlerts = buildFacilityThreatAlerts(result, facilityIso3);

    const fsatData = buildFacilityFsatData(result);

    return (
        <div className="space-y-8 animate-fade-in">
            {onRefreshConfidence && <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs text-slate-600">Refresh threat confidence using dated sources. Evidence gaps remain unscored.</p>
                <button type="button" disabled={refreshingConfidence} onClick={onRefreshConfidence}
                    className="rounded-lg bg-indigo-700 px-4 py-2 text-xs font-bold text-white disabled:opacity-50">
                    {refreshingConfidence ? 'Researching evidence…' : 'Refresh Confidence'}
                </button>
            </div>}
            {confidenceRefreshError && <p role="alert" className="text-sm text-red-700">{confidenceRefreshError}</p>}
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-blue-600">{result.workflowVersion || 'SRM-02-v2'}</p>
                        <h3 className="font-bold text-slate-800 text-lg uppercase tracking-tight">Executive Summary</h3>
                    </div>
                    <div className="text-right">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${result.riskLevel === 'High' || result.riskLevel === 'Critical' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            Risk Level: {result.riskLevel}
                          </span>
                    </div>
                </div>
                <div className="prose prose-sm text-slate-700 max-w-none" dangerouslySetInnerHTML={{ __html: md.render(result.executiveSummary || '') }} />
                <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                        <h4 className="text-xs font-bold uppercase tracking-wider text-red-700">Top Vulnerabilities</h4>
                        <ul className="mt-2 space-y-1 text-sm text-slate-700">
                            {(result.topVulnerabilities || []).map((item, index) => <li key={index}>• {item}</li>)}
                        </ul>
                    </div>
                    <div>
                        <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-700">Immediate Actions</h4>
                        <ul className="mt-2 space-y-1 text-sm text-slate-700">
                            {(result.immediateActions || []).map((item, index) => <li key={index}>• {item}</li>)}
                        </ul>
                    </div>
                </div>
            </div>

            {hasValidLocation && (
                <Section title="Asset Geography & Security Context">
                     <div className="w-full h-80 rounded-xl overflow-hidden border border-slate-300 relative shadow-inner">
                        <UnifiedMap
                            isLoaded={isLoaded}
                            initialViewState={{ longitude: result.location.lng, latitude: result.location.lat, zoom: 15 }}
                            theme="light"
                            markers={mapMarkers}
                            alerts={localThreatAlerts}
                            filterIso3={facilityIso3}
                        />
                    </div>
                    <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                        {ICONS.location} {result.location.address}
                    </p>
                </Section>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                    <h4 className="font-bold text-slate-700 mb-2 flex items-center gap-2">
                         <span className="p-1 bg-blue-100 text-blue-600 rounded">P</span> Perimeter Control
                    </h4>
                    <p className="text-sm text-slate-600 leading-relaxed">{securityControls.perimeter || 'Perimeter analysis unavailable.'}</p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                    <h4 className="font-bold text-slate-700 mb-2 flex items-center gap-2">
                         <span className="p-1 bg-emerald-100 text-emerald-600 rounded">A</span> Access Control
                    </h4>
                    <p className="text-sm text-slate-600 leading-relaxed">{securityControls.accessControl || 'Access-control analysis unavailable.'}</p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                    <h4 className="font-bold text-slate-700 mb-2 flex items-center gap-2">
                         <span className="p-1 bg-amber-100 text-amber-600 rounded">S</span> Surveillance
                    </h4>
                    <p className="text-sm text-slate-600 leading-relaxed">{securityControls.surveillance || 'Surveillance analysis unavailable.'}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                <Section title="Critical Asset Inventory">
                    <div className="space-y-3">
                        {keyAssets.map((asset: { name: string, criticality: string, description: string }, i: number) => (
                            <div key={i} className="p-4 bg-slate-50 rounded-lg border border-slate-200 flex justify-between items-center group hover:bg-white transition-colors">
                                <div>
                                    <h5 className="font-bold text-slate-800">{asset.name}</h5>
                                    <p className="text-xs text-slate-500">{asset.description}</p>
                                </div>
                                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${asset.criticality === 'Critical' ? 'bg-red-600 text-white' : 'bg-slate-200 text-slate-700'}`}>
                                    {asset.criticality}
                                </span>
                            </div>
                        ))}
                        {keyAssets.length === 0 && (
                            <p className="p-4 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-800">Critical asset analysis was incomplete. Regenerate this report.</p>
                        )}
                    </div>
                </Section>

                <Section title="Tactical Threat Mitigation">
                    <div className="space-y-4">
                        {threatAssessment.map((threat: { threat: string, likelihood: string, impact: string, mitigation: string }, i: number) => (
                            <div key={i} className="p-4 bg-white rounded-lg border border-slate-200 shadow-sm">
                                <div className="flex justify-between items-start mb-2">
                                    <h5 className="font-bold text-slate-800 text-sm">{threat.threat}</h5>
                                    <div className="flex gap-1">
                                         <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded border">L: {threat.likelihood}</span>
                                         <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded border">I: {threat.impact}</span>
                                    </div>
                                </div>
                                <div className="p-2 bg-blue-50/50 rounded text-xs text-blue-900 border border-blue-100 leading-relaxed">
                                    <strong>Countermeasure:</strong> {threat.mitigation}
                                </div>
                            </div>
                        ))}
                        {threatAssessment.length === 0 && (
                            <p className="p-4 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-800">Tactical threat analysis was incomplete. Regenerate this report.</p>
                        )}
                    </div>
                </Section>
            </div>

            {assetRegister.length > 0 && (
                <Section title="Four-Tier Asset Register">
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        {assetRegister.map((group, groupIndex) => (
                            <div key={`${group.tier}-${groupIndex}`} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                                <div className="border-b border-slate-200 bg-slate-900 px-4 py-3 text-white">
                                    <h4 className="font-bold">{group.tier}</h4>
                                    <p className="mt-1 text-xs text-slate-300">{group.description}</p>
                                </div>
                                <div className="divide-y divide-slate-100">
                                    {(group.assets || []).map((asset, assetIndex) => (
                                        <div key={`${asset.name}-${assetIndex}`} className="p-4">
                                            <div className="flex items-start justify-between gap-3">
                                                <h5 className="font-semibold text-slate-900">{asset.name}</h5>
                                                <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold uppercase text-slate-700">{asset.criticality}</span>
                                            </div>
                                            <dl className="mt-2 grid grid-cols-1 gap-1 text-xs text-slate-600 sm:grid-cols-2">
                                                <div><dt className="font-bold text-slate-700">Access</dt><dd>{asset.accessRequirements}</dd></div>
                                                <div><dt className="font-bold text-slate-700">Classification</dt><dd>{asset.classification}</dd></div>
                                            </dl>
                                            <p className="mt-2 text-xs text-slate-500">{asset.exposureNotes}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {threatAssetMatrix.length > 0 && (
                <Section title="Threat-Asset Vulnerability Matrix">
                    <div className="overflow-x-auto rounded-xl border border-slate-200">
                        <table className="min-w-full divide-y divide-slate-200 text-sm">
                            <thead className="bg-slate-900 text-left text-xs uppercase tracking-wider text-white">
                                <tr>
                                    <th className="px-4 py-3">Asset</th>
                                    <th className="px-4 py-3">Threat and Vulnerability</th>
                                    <th className="px-4 py-3">L</th>
                                    <th className="px-4 py-3">I</th>
                                    <th className="px-4 py-3">Score</th>
                                    <th className="px-4 py-3">Countermeasure</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 bg-white">
                                {threatAssetMatrix.map((item, index) => (
                                    <tr key={`${item.asset}-${item.threat}-${index}`}>
                                        <td className="px-4 py-3 align-top"><p className="font-semibold text-slate-900">{item.asset}</p><p className="text-xs text-slate-500">{item.assetTier}</p></td>
                                        <td className="px-4 py-3 align-top"><p className="font-semibold text-slate-800">{item.threat}</p><p className="mt-1 text-xs text-slate-500">{item.vulnerability}</p></td>
                                        <td className="px-4 py-3 align-top">{item.likelihood}</td>
                                        <td className="px-4 py-3 align-top">{item.impact}</td>
                                        <td className="px-4 py-3 align-top"><span className={`rounded-full px-2 py-1 text-xs font-bold ${item.riskScore >= 15 ? 'bg-red-100 text-red-700' : item.riskScore >= 8 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{item.riskScore}</span></td>
                                        <td className="px-4 py-3 align-top text-xs text-slate-600">{item.countermeasure}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Section>
            )}

            {countermeasurePlan.length > 0 && (
                <Section title="Countermeasure Plan">
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                        {countermeasurePlan.map((item, index) => (
                            <div key={`${item.control}-${index}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs font-bold uppercase tracking-wider text-blue-700">{item.category}</span>
                                    <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold uppercase text-slate-700">{item.costTier} Cost</span>
                                </div>
                                <h4 className="mt-3 font-bold text-slate-900">{item.control}</h4>
                                <p className="mt-2 text-xs leading-relaxed text-slate-600">{item.rationale}</p>
                                <p className="mt-3 text-xs font-semibold text-slate-700">Priority: {item.priority}</p>
                                <p className="mt-1 text-xs text-slate-500">{(item.assetTiers || []).join(', ')}</p>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            <div className="grid grid-cols-1 gap-8 xl:grid-cols-2">
                <Section title="Regulatory Compliance Gaps">
                    {complianceGaps.length > 0 ? (
                        <div className="space-y-3">
                            {complianceGaps.map((item, index) => (
                                <div key={`${item.framework}-${index}`} className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <h4 className="font-bold text-amber-950">{item.framework}</h4>
                                        <span className="text-xs font-bold uppercase text-amber-800">{item.priority}</span>
                                    </div>
                                    <p className="mt-2 text-sm text-amber-950">{item.requirement}</p>
                                    <p className="mt-2 text-xs text-amber-800"><strong>Gap:</strong> {item.gap}</p>
                                    <p className="mt-1 text-xs text-amber-800"><strong>Action:</strong> {item.action}</p>
                                    <p className="mt-1 text-xs text-amber-700"><strong>Target:</strong> {item.deadline}</p>
                                </div>
                            ))}
                        </div>
                    ) : <p className="text-sm text-slate-500">No frameworks selected. Consultant must confirm applicable obligations.</p>}
                </Section>

                <Section title="90 / 180 / 365-Day Roadmap">
                    <div className="space-y-3">
                        {roadmap.map((item, index) => (
                            <div key={`${item.phase}-${index}`} className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <h4 className="font-bold text-emerald-950">{item.phase}</h4>
                                    <span className="text-xs font-bold uppercase text-emerald-800">{item.budgetTier} Budget</span>
                                </div>
                                <p className="mt-2 text-sm font-semibold text-emerald-950">{item.objective}</p>
                                <ul className="mt-2 space-y-1 text-xs text-emerald-900">
                                    {(item.actions || []).map((action, actionIndex) => <li key={actionIndex}>• {action}</li>)}
                                </ul>
                                <p className="mt-2 text-xs text-emerald-800"><strong>Owner:</strong> {item.owner}</p>
                            </div>
                        ))}
                    </div>
                </Section>
            </div>

            {(result.siteContext || enrichmentStatus.length > 0) && (
                <Section title="Research Coverage and Evidence Gaps">
                    <p className="text-sm text-slate-700">{result.siteContext?.summary}</p>
                    <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <div>
                            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">Perimeter Notes</h4>
                            <ul className="mt-2 space-y-1 text-sm text-slate-600">
                                {(result.siteContext?.perimeterNotes || []).map((item, index) => <li key={index}>• {item}</li>)}
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-xs font-bold uppercase tracking-wider text-amber-700">Evidence Gaps</h4>
                            <ul className="mt-2 space-y-1 text-sm text-slate-600">
                                {(result.siteContext?.evidenceGaps || []).map((item, index) => <li key={index}>• {item}</li>)}
                            </ul>
                        </div>
                    </div>
                    {enrichmentStatus.length > 0 && (
                        <div className="mt-5 grid grid-cols-1 gap-2 md:grid-cols-3">
                            {enrichmentStatus.map((item, index) => (
                                <div key={`${item.process}-${index}`} className="rounded-lg border border-slate-200 p-3">
                                    <div className="flex items-center justify-between gap-2"><p className="text-xs font-bold text-slate-800">{item.process}</p><span className="text-[10px] font-bold uppercase text-slate-500">{item.status}</span></div>
                                    <p className="mt-1 text-xs text-slate-500">{item.summary}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </Section>
            )}

            {onStartFsatAudit && (
                <div className="mt-12 bg-indigo-900 rounded-2xl p-8 text-white relative overflow-hidden shadow-2xl border border-indigo-500/30">
                    <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>

                    <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                        <div className="max-w-xl text-center md:text-left">
                            <h3 className="text-2xl font-bold mb-3 flex items-center justify-center md:justify-start gap-3">
                                <span className="p-2 bg-indigo-500/30 rounded-lg text-indigo-300">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                                </span>
                                Operationalize This Strategy
                            </h3>
                            <p className="text-indigo-100/80 leading-relaxed mb-0">
                                Transition from strategic planning to a formal **Facility Security Assessment (FSAT)**. 
                                Our AI will pre-fill the audit framework with the intelligence generated above, allowing for rapid verification and compliance mapping.
                            </p>
                        </div>
                        <button 
                            onClick={() => onStartFsatAudit(fsatData)}
                            className="whitespace-nowrap bg-white text-indigo-900 hover:bg-slate-50 px-8 py-4 rounded-xl font-black text-lg transition-all shadow-[0_10px_40px_rgba(255,255,255,0.2)] hover:scale-[1.05] active:scale-95 flex items-center gap-3 group"
                        >
                            Start Automated Audit
                            <span className="transition-transform group-hover:translate-x-1">&rarr;</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FacilityProtectionDisplay;
